import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const TermsConditions = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link
          to="/"
          className="inline-flex items-center text-orange-500 hover:text-orange-600 mb-8"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Home
        </Link>

        <div className="bg-white rounded-xl shadow-lg p-8">
          <div className="flex justify-between items-start mb-6">
            <h1 className="text-2xl font-bold">Terms & Conditions</h1>
            <p className="text-gray-600 text-sm">Last updated: December 2024</p>
          </div>

          <div className="prose max-w-none text-sm">
            <h2 className="font-bold text-base mb-4">Introduction</h2>
            <p className="mb-6">
              Welcome to Liya Aathira Private Limited. These terms and
              conditions outline the rules and regulations for the use of our
              website and services. By accessing or using our website, you agree
              to comply with these terms. If you do not agree with any part of
              these terms, you must not use our website.
            </p>

            <h2 className="font-bold text-base mb-4">Definitions</h2>
            <ul className="mb-6 ml-6 list-disc">
              <li>
                <span className="font-bold">Company</span>
                <br />
                Refers to Liya Aathira Private Limited, located at Liya Aathira
                Private Limited. 2/25, 25th Street Thillaiganganagar,
                Nanganallur, Chennai, Tamil Nadu, India 600061.
              </li>
              <li>
                <span className="font-bold">Services</span>
                <br />
                Includes data processing, analysis, consulting, and any related
                services provided by the Company.
              </li>
              <li>
                <span className="font-bold">Client</span>
                <br />
                Refers to any individual or entity that uses our Services.
              </li>
            </ul>

            <h2 className="font-bold text-base mb-4">Use of Website</h2>
            <ul className="mb-6 ml-6 list-disc">
              <li>You agree to use our website only for lawful purposes.</li>
              <li>
                You must not use the website in any way that may impair its
                functionality or accessibility.
              </li>
              <li>
                Unauthorized use of this website may result in legal action.
              </li>
            </ul>

            <h2 className="font-bold text-base mb-4">Data Privacy</h2>
            <ul className="mb-6 ml-6 list-disc">
              <li>
                We adhere to applicable data protection laws and handle your
                data responsibly.
              </li>
              <li>
                Please review our Privacy Policy to understand how we collect,
                use, and protect your information.
              </li>
              <li>
                By using our Services, you consent to the processing of your
                data as described in the Privacy Policy.
              </li>
            </ul>

            <h2 className="font-bold text-base mb-4">Intellectual Property</h2>
            <ul className="mb-6 ml-6 list-disc">
              <li>
                All content on the website, including text, graphics, logos, and
                software, is the property of the Company or its licensors.
              </li>
              <li>
                You may not reproduce, distribute, or create derivative works
                from any content without prior written permission.
              </li>
            </ul>

            <h2 className="font-bold text-base mb-4">Service Terms</h2>
            <ul className="mb-6 ml-6 list-disc">
              <li>
                The Company provides data processing and consulting services as
                agreed upon in individual contracts with clients.
              </li>
              <li>
                The scope, timelines, and deliverables of Services will be
                detailed in a formal agreement.
              </li>
            </ul>

            <h2 className="font-bold text-base mb-4">Marketing and Logo Usage</h2>
            <ul className="mb-6 ml-6 list-disc">
              <li>
                Customer grants the Company permission to use Customer's name and logo for marketing purposes, including but not limited to website testimonials, case studies, and promotional materials.
              </li>
              <li>
                Customer may revoke this permission with 30 days written notice to the Company.
              </li>
            </ul>

            <h2 className="font-bold text-base mb-4">Payment and Fees</h2>
            <ul className="mb-6 ml-6 list-disc">
              <li>
                Clients agree to pay the fees outlined in their service
                agreement.
              </li>
              <li>
                Payments must be made within the specified timeframe to avoid
                service interruptions.
              </li>
            </ul>

            <h2 className="font-bold text-base mb-4">
              Limitation of Liability
            </h2>
            <ul className="mb-6 ml-6 list-disc">
              <li>
                The Company is not liable for indirect, incidental, or
                consequential damages arising from the use of our Services.
              </li>
              <li>
                Our liability is limited to the amount paid by the Client for
                the Services rendered.
              </li>
            </ul>

            <h2 className="font-bold text-base mb-4">Termination</h2>
            <ul className="mb-6 ml-6 list-disc">
              <li>
                The Company reserves the right to terminate or suspend access to
                our Services at our discretion.
              </li>
              <li>
                Clients may terminate their agreement by providing written
                notice, subject to the terms of their contract.
              </li>
            </ul>

            <h2 className="font-bold text-base mb-4">Governing Law</h2>
            <ul className="mb-6 ml-6 list-disc">
              <li>These terms are governed by the laws of the Government.</li>
              <li>
                Any disputes will be resolved exclusively in the courts of the
                Government.
              </li>
            </ul>

            <h2 className="font-bold text-base mb-4">
              Return and Refund Policy
            </h2>

            <h3 className="font-bold text-base mb-4">
              Eligibility for Returns and Refunds
            </h3>
            <ul className="mb-6 ml-6 list-disc">
              <li>
                Refunds are applicable only for services where a formal
                agreement specifies refund conditions.
              </li>
              <li>
                Returns and refunds are not applicable to completed or partially
                completed data processing projects.
              </li>
            </ul>

            <h3 className="font-bold text-base mb-4">Request Process</h3>
            <ol className="mb-6 ml-6 list-decimal">
              <li>
                Clients must submit a refund request in writing to support
                email.
              </li>
              <li>
                The request must include the reason for the refund and any
                supporting documentation.
              </li>
              <li>
                Requests must be submitted within 15 days of the service
                delivery date.
              </li>
            </ol>

            <h3 className="font-bold text-base mb-4">Refund Approval</h3>
            <ul className="mb-6 ml-6 list-disc">
              <li>
                Refunds are subject to the Company's approval based on the
                validity of the request.
              </li>
              <li>
                Approved refunds will be processed within 10 business days.
              </li>
              <li>Refunds will be issued to the original payment method.</li>
            </ul>

            <h3 className="font-bold text-base mb-4">
              Non-Refundable Services
            </h3>
            <ul className="mb-6 ml-6 list-disc">
              <li>Custom data processing tasks.</li>
              <li>
                Services where work has already commenced or been delivered.
              </li>
            </ul>

            <h3 className="font-bold text-base mb-4">
              Contact for Returns and Refunds
            </h3>
            <p className="mb-6">
              For any inquiries,{" "}
              <Link to="/contact" className="text-orange-500 hover:underline">
                Contact Us
              </Link>
              .
            </p>

            <h2 className="font-bold text-base mb-4">Modifications to Terms</h2>
            <ul className="mb-6 ml-6 list-disc">
              <li>
                The Company reserves the right to modify these terms at any
                time.
              </li>
              <li>
                Continued use of our Services after changes indicates acceptance
                of the revised terms.
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsConditions;
